import type { MapAreaPoint, MapFeatures, SweepAreaSuctionMode } from '../../../../../../../types/MapArea.types';
import {
  CleaningFeatureMarker,
  CleaningFeatureSvg,
  CleaningGlassLine,
  CleaningNoGoAreaPolygon,
  CleaningNoGoLine,
  CleaningSpecialZoneBadge,
  CleaningSpecialZoneMode,
  CleaningSpecialZonePolygon,
  CleaningSpecialZoneTimes,
  CleaningStartMarkerContainer,
  CleaningStartMarkerGlow,
  CleaningStartMarkerHomeIcon,
  CleaningStartMarkerKiraIcon,
} from './CleaningAreaMapOverlay.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { ISvgIconName } from 'lib/components/SvgIcon/interfaces/SvgIconNames';

const toPoints = (polygon: MapAreaPoint[]): string => polygon.map(point => `${point.x},${point.y}`).join(' ');

const SUCTION_MODE_ICON_NAMES: Record<SweepAreaSuctionMode, ISvgIconName> = {
  normal: 'normalMode',
  eco: 'ecoMode',
  boost: 'boostMode',
};

/**
 * Non-interactive overlay for a CV50 map's start/charging points, special cleaning zones, no-go
 * zones/lines and glass lines; RoomArea outlines render above this and stay clickable.
 * Stacked back-to-front (SVG paint order): no-go line, no-go zone, glass line, special zone, home.
 */
export const CleaningAreaMapFeatures = ({ features }: { features: MapFeatures }): JSX.Element => {
  const { startPoints, specialZones, noGoAreas, noGoLines, glassLines } = features;

  return (
    <>
      <CleaningFeatureSvg viewBox="0 0 100 100" preserveAspectRatio="none">
        {noGoLines.map((line, index) => (
          <CleaningNoGoLine
            key={`nogo-line-${index}`}
            x1={line.from.x}
            y1={line.from.y}
            x2={line.to.x}
            y2={line.to.y}
          />
        ))}
        {noGoAreas.map((area, index) => (
          <CleaningNoGoAreaPolygon key={`nogo-area-${index}`} points={toPoints(area.polygon)} />
        ))}
      </CleaningFeatureSvg>

      <CleaningFeatureSvg viewBox="0 0 100 100" preserveAspectRatio="none">
        {glassLines.map((line, index) => (
          <CleaningGlassLine
            key={`glass-line-${index}`}
            x1={line.from.x}
            y1={line.from.y}
            x2={line.to.x}
            y2={line.to.y}
          />
        ))}
        {specialZones.map(zone => (
          <CleaningSpecialZonePolygon key={zone.id} points={toPoints(zone.polygon)} />
        ))}
      </CleaningFeatureSvg>

      {specialZones.map(zone => (
        <CleaningFeatureMarker
          key={`${zone.id}-badge`}
          $rotationDeg={zone.rotationDeg}
          style={{ left: `${zone.position.x}%`, top: `${zone.position.y}%` }}
        >
          <CleaningSpecialZoneBadge>
            <CleaningSpecialZoneTimes>{zone.times}</CleaningSpecialZoneTimes>
            <CleaningSpecialZoneMode>
              <SvgIcon name={SUCTION_MODE_ICON_NAMES[zone.suctionMode]} />
            </CleaningSpecialZoneMode>
          </CleaningSpecialZoneBadge>
        </CleaningFeatureMarker>
      ))}

      {startPoints.map(point => (
        <CleaningFeatureMarker key={point.id} style={{ left: `${point.position.x}%`, top: `${point.position.y}%` }}>
          <CleaningStartMarkerContainer>
            <CleaningStartMarkerHomeIcon>
              <SvgIcon name="home" />
            </CleaningStartMarkerHomeIcon>
            <CleaningStartMarkerGlow />
            <CleaningStartMarkerKiraIcon>
              <SvgIcon name="kiraCV50" />
            </CleaningStartMarkerKiraIcon>
          </CleaningStartMarkerContainer>
        </CleaningFeatureMarker>
      ))}
    </>
  );
};
